import {
  Box,
  Typography,
  InputAdornment,
  Stack,
  Button,
  useTheme,
  Grid,
  IconButton,
} from "@mui/material";
import { CustomInput } from "global/components";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useState } from "react";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import deepClone from "lodash/cloneDeep";
import _ from "lodash";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { addUpdateContactTag } from "../TagsService";

interface CustomProps {
  contactId?: number;
  isEdit: boolean;
  handleDialogClose: Function;
  isVisible: boolean;
  callBack: Function;
}

const AddUpdateTag = (props: CustomProps) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addTagsValue, setTagsValue] = useState<{
    value: string;
    error: string;
  }>({
    value: "",
    error: "",
  });
  const [finalTags, setFinalTags] = useState<{ id: string; name: string }[]>(
    []
  );

  const validateTags = () => {
    let isValid = true;
    const clonedAddTagsValue = deepClone(addTagsValue);
    const checkExistingValue = finalTags?.some(
      (item: any) => item?.name === (addTagsValue?.value).toLowerCase().trim()
    );
    if (!isTruthy(addTagsValue?.value)) {
      clonedAddTagsValue.error = "Tag cant be empty";
      isValid = false;
    }
    if (checkExistingValue) {
      clonedAddTagsValue.error = "You cannot add duplicate tags";
      isValid = false;
    }
    setTagsValue({ ...clonedAddTagsValue });
    return isValid;
  };

  const addTagsHandler = (e: any) => {
    if (validateTags()) {
      if (e.key === "Enter") {
        e.preventDefault();
        const addedTags: any[] = [];
        addedTags.push(...finalTags, {
          id: "NEW",
          name: addTagsValue?.value?.toLowerCase().trim(),
        });
        const filterValue = _.uniqWith(addedTags, _.isEqual);
        setFinalTags(filterValue);
        setTagsValue({ value: "", error: "" });
      }
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    const updatedTags = finalTags.filter((tag) => tag.name !== tagToDelete);
    setFinalTags(updatedTags);
  };

  const getDialogHeader = () => {
    return (
      <Box display="flex" justifyContent="center" pt={3}>
        <Typography variant="h4" color={theme.palette.primary.main}>
          {props.isEdit ? "Update tag" : "Create tag"}
        </Typography>
      </Box>
    );
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const res = await addUpdateContactTag(
        props?.contactId,
        finalTags.map((data) => data.name.trim())
      );
      openSuccessNotification(res.message);
      setIsLoading(false);
      props.handleDialogClose();
      await props.callBack();
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getBody = () => {
    return (
      <Stack
        display="flex"
        flexDirection="column"
        justifyContent="center"
        spacing={1}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle2" textAlign="justify">
            Tags help you organize and track valuable insights about your
            contacts. Use them to identify where a contact came from, which
            campaigns influenced their engagement, and more.
          </Typography>
          <Box pt={1}>
            <CustomInput
              id="tag"
              placeHolder="Enter tag name and press enter"
              required
              type="text"
              name="tag"
              onChange={(event: any) =>
                setTagsValue({ value: event.target.value, error: "" })
              }
              onKeyPress={(e: any) => addTagsHandler(e)}
              value={addTagsValue.value}
              propsToInputElement={{ maxLength: 100 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {addTagsValue.value.trim()?.length} / 100
                    </span>
                  </InputAdornment>
                ),
              }}
            />
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ fontSize: "0.75rem", ml: 1.5 }}
            >
              Type your tag and press enter to add.
            </Typography>
          </Box>
        </Stack>

        <Box>
          {addTagsValue.error && finalTags.length === 0 && (
            <Typography color="error" variant="body2">
              Tag cant be empty
            </Typography>
          )}
        </Box>

        <Grid container>
          {finalTags.map((item: any, index: number) => (
            <Grid item key={index} pl={0} pr={2}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  backgroundColor: "#F4F1FC",
                  p: { xs: 1, md: 1.5 },
                  borderRadius: "5px",
                }}
              >
                <Typography variant="subtitle1" color="#7551D0">
                  {item.name}
                </Typography>
                <IconButton
                  sx={{ p: 0 }}
                  onClick={() => handleDeleteTag(item.name)}
                >
                  <CloseRoundedIcon htmlColor="#7551D0" fontSize="small" />
                </IconButton>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Stack>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={2} mb={1}>
        <Button
          id="contacts_add__cancel_btn"
          variant={"outlined"}
          sx={{ width: "7.125rem", height: "2.5rem" }}
          onClick={() => props.handleDialogClose()}
        >
          Cancel
        </Button>
        <Button
          id="contacts_add_save_btn"
          variant={"contained"}
          onClick={() => handleSave()}
          sx={{ width: "7.125rem", height: "2.5rem" }}
          disabled={finalTags?.length < 1}
        >
          Save
        </Button>
      </Stack>
    );
  };
  return (
    <>
      <CustomDialog
        handleDialogClose={props.handleDialogClose}
        isDialogOpen={props.isVisible}
        title={getDialogHeader()}
        body={getBody()}
        footer={getFooter()}
        closeButtonVisibility={true}
        closable
        isNavsHide={false}
        maxWidth="sm"
        persistOnClickOutside
        persistOnEsc
      />
      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default AddUpdateTag;
